import * as XLSXStyle from 'xlsx-js-style';
import { displayDate } from './MiscFunctions';
import { FieldType } from '../../types';
import { globalValue } from '../apps/AppContext';
import dayjs from 'dayjs';

interface ExportColumn<T> {
  Header: string;
  accessor: keyof T | ((row: T) => any);
  fieldType?: FieldType
}

const getHeaderRows = (sheetTitle?: string, periodLabel?: string): string[][] => {
  const bizDetails = globalValue?.bizMaster?.bizDetails;
  const executionTime = dayjs().format('DD-MMM-YYYY hh:mm A');
  const rows: string[][] = [];
  if (bizDetails?.bizName) rows.push([bizDetails.bizName]);
  if (bizDetails?.address) rows.push([bizDetails.address]);
  if (sheetTitle) rows.push([sheetTitle]);
  if (periodLabel) rows.push([periodLabel]);
  rows.push([`Generated: ${executionTime}`]);
  rows.push([]); // blank row before data
  return rows;
};

const exportTableToExcel = <T extends object>(
  columns: ExportColumn<T>[],
  data: T[],
  fileName: string = 'table.xlsx',
  title?: string,
  totals?: Record<string, number>,
  periodLabel?: string,
) => {
  const resolveAccessor = (obj: any, accessor: string | ((row: any) => any)) => {
    if (typeof accessor === 'string') {
      const value = accessor.split('.').reduce((acc, part) => acc && acc[part], obj);
      return value === undefined ? '' : value;
    } else if (typeof accessor === 'function') {
      const value = accessor(obj);
      return value === undefined ? '' : value;
    }
    return '';
  };

  if (columns.length === 0) return;

  const sheetTitle = title || fileName.replace(/\.\w+$/, '');
  const headerRows = getHeaderRows(sheetTitle, periodLabel);
  const headerRowCount = headerRows.length;
  const colCount = columns.length;

  const makeCell = (
    value: string | number,
    bold: boolean,
    isHeader = false
  ): XLSXStyle.CellObject => ({
    v: value,
    t: typeof value === 'number' ? 'n' : 's',
    s: {
      font: {
        bold,
        ...(isHeader ? { color: { rgb: 'FFFFFF' } } : {}),
      },
      ...(isHeader
        ? { fill: { fgColor: { rgb: '2E4057' }, patternType: 'solid' } }
        : {}),
    },
  });

  // Meta rows as plain text cells
  const metaCellRows: XLSXStyle.CellObject[][] = headerRows.map(row =>
    [makeCell(row[0] || '', false)]
  );

  // Column header row
  const headerCellRow: XLSXStyle.CellObject[] = columns.map(col =>
    makeCell(col.Header, true, true)
  );

  // Data rows
  const dataCellRows: XLSXStyle.CellObject[][] = data.map(row => {
    const isHighlight = !!(row as any).isHighlight;
    return columns.map(col => {
      const value = resolveAccessor(row, col.accessor as string);
      let cellValue: string | number;

      if (col.fieldType === FieldType.date || value instanceof Date) {
        const dateObj = value instanceof Date ? value : (typeof value === 'number' ? new Date(value) : undefined);
        cellValue = displayDate(dateObj);
      } else if (typeof value === 'string' && !isNaN(Date.parse(value)) && isNaN(Number(value))) {
        cellValue = String(value);
      } else if (typeof value === 'number') {
        cellValue = value % 1 === 0 ? value : parseFloat(value.toFixed(2));
      } else {
        cellValue = String(value);
      }

      return makeCell(cellValue, isHighlight);
    });
  });

  const totalsCellRow: XLSXStyle.CellObject[] | null =
    totals && Object.keys(totals).length > 0
      ? columns.map((col, i) => {
          const accessor = typeof col.accessor === 'string' ? col.accessor : '';
          const hasTotal = accessor && accessor in totals;
          const value = hasTotal ? totals[accessor] : i === 0 ? 'Total' : '';
          return {
            v: value,
            t: typeof value === 'number' ? 'n' : 's',
            s: { font: { bold: true } },
          } as XLSXStyle.CellObject;
        })
      : null;

  const allRows: XLSXStyle.CellObject[][] = [
    ...metaCellRows,
    headerCellRow,
    ...dataCellRows,
    ...(totalsCellRow ? [totalsCellRow] : []),
  ];

  const ws: XLSXStyle.WorkSheet = {};
  allRows.forEach((row, ri) => {
    row.forEach((cell, ci) => {
      const addr = XLSXStyle.utils.encode_cell({ r: ri, c: ci });
      ws[addr] = cell;
    });
  });
  ws['!ref'] = XLSXStyle.utils.encode_range({
    s: { r: 0, c: 0 },
    e: { r: allRows.length - 1, c: colCount - 1 },
  });

  // Auto-fit column widths based on content
  ws['!cols'] = columns.map(col => {
    const headerLen = col.Header.length;
    const maxDataLen = data.reduce((max, row) => {
      const val = resolveAccessor(row, col.accessor as string);
      const len = val !== null && val !== undefined ? String(val).length : 0;
      return Math.max(max, len);
    }, 0);
    return { wch: Math.min(40, Math.max(10, headerLen, maxDataLen)) };
  });

  // Freeze the header row (skip meta rows + 1 header row)
  ws['!freeze'] = { xSplit: 0, ySplit: headerRowCount + 1 };

  // Sheet name: sanitize title to max 31 chars (Excel limit)
  const safeSheetName = sheetTitle.replace(/[\\/*?[\]:]/g, '').slice(0, 31) || 'Report';

  const wb = XLSXStyle.utils.book_new();
  XLSXStyle.utils.book_append_sheet(wb, ws, safeSheetName);
  XLSXStyle.writeFile(wb, fileName);
};

export default exportTableToExcel;
